import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ApiError } from "../../lib/api";
import { strings } from "../../lib/strings";
import { usePageTitle } from "../../lib/title";
import { fill } from "../../lib/words";
import { Button } from "./button";

/**
 * The states a screen draws besides its work (BUILD_SPEC §4.6): what couldn't be reached, that nothing was lost, and
 * a way to ask again; what isn't there any more; what isn't the person's to do, and who can do it; and a filter that
 * finds nothing, with the way to clear it. Each in place of the work it stands for, never a page sent elsewhere; one
 * that stands for a whole page (`page`) is its heading, and names it in the browser's title.
 */

const words = strings.states;

/** "the release" as the start of a sentence. */
const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function Panel({ children, role }: { children: ReactNode; role?: "alert" | "status" }) {
  return (
    <div role={role} className="rounded-xl border border-dashed border-[hsl(var(--border))] px-6 py-10 text-center">
      {children}
    </div>
  );
}

/** A state's title: the page's own heading where it stands for the page, else a line of the panel. */
function Title({ text, page }: { text: string; page: boolean }) {
  usePageTitle(page ? text : null);
  return page ? <h1 className="text-sm font-medium">{text}</h1> : <p className="text-sm font-medium">{text}</p>;
}

/** What couldn't be reached, that nothing was lost, and a retry. */
export function Unavailable({ what, onRetry, page = false }: { what: string; onRetry: () => void; page?: boolean }) {
  return (
    <Panel role="alert">
      <Title text={fill(words.unavailable.title, { what })} page={page} />
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{words.unavailable.body}</p>
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
        {words.unavailable.retry}
      </Button>
    </Panel>
  );
}

/** Something asked for by its address that isn't there: deleted, moved, or never was. */
export function NotFound({ what, page = false }: { what: string; page?: boolean }) {
  return (
    <Panel role="status">
      <Title text={fill(words.notFound.title, { what: sentence(what) })} page={page} />
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{words.notFound.body}</p>
      <Button asChild variant="outline" size="sm" className="mt-4">
        <Link to="/admin">{words.notAllowed.home}</Link>
      </Button>
    </Panel>
  );
}

/** What isn't the person's to see or do, and who can: in place of a page that would only refuse them. */
export function NotAllowed({
  who,
  what,
  body = words.notAllowed.body,
  action,
  page = false,
}: {
  who: string;
  what: string;
  body?: string;
  action?: ReactNode;
  page?: boolean;
}) {
  return (
    <Panel role="status">
      <Title text={fill(words.notAllowed.title, { who, what })} page={page} />
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{body}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {action}
        <Button asChild variant="outline" size="sm">
          <Link to="/admin">{words.notAllowed.home}</Link>
        </Button>
      </div>
    </Panel>
  );
}

/** A page of Steer's own settings, for its Administrators: what it is, and who can, for anyone else. */
export function AdministratorsOnly({ what }: { what: string }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <NotAllowed who={words.who.administrators} what={what} page />
    </div>
  );
}

/** A filter that finds nothing: said so, with the way to clear it. */
export function NoResults({ onClear, title = words.noResults.title }: { onClear: () => void; title?: string }) {
  return (
    <Panel role="status">
      <p className="text-sm font-medium">{title}</p>
      <Button variant="outline" size="sm" className="mt-4" onClick={onClear}>
        {words.noResults.clear}
      </Button>
    </Panel>
  );
}

/** Whether a query's refusal says the thing asked for isn't there. */
export const isNotFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/**
 * A query's answer as a screen draws it: its work once it has come, kept while it is asked again; its skeleton until
 * then; and if it can't be had, what couldn't be loaded with a retry, or that it isn't there.
 */
export function QueryGate<T>({
  query,
  what,
  skeleton,
  notFound,
  page = false,
  children,
}: {
  query: Pick<UseQueryResult<T>, "data" | "error" | "refetch">;
  /** What the screen loads, as "Couldn't load {what}" says it. */
  what: string;
  skeleton: ReactNode;
  /** What to draw for a 404, if the screen names it otherwise: by default, that it isn't here. */
  notFound?: ReactNode;
  /** Whether the states stand for the whole page, which then has them as its heading. */
  page?: boolean;
  children: (data: T) => ReactNode;
}) {
  if (query.data !== undefined) return <>{children(query.data)}</>;
  if (query.error !== null && query.error !== undefined) {
    if (isNotFound(query.error)) return <>{notFound ?? <NotFound what={what} page={page} />}</>;
    return <Unavailable what={what} onRetry={() => void query.refetch()} page={page} />;
  }
  return <>{skeleton}</>;
}
